import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiPause, FiPlay } from 'react-icons/fi';
import { apiJson, ApiError } from '@/lib/api';
import { track } from '@/lib/analytics';
import type {
  MetaOpsAction,
  MetaOpsActionsResponse,
  MetaOpsCampaignsResponse,
  MetaOpsScorecard,
  MetaOpsSettings,
  MetaOpsStandingOrders,
} from '@/lib/types';

const RANGE_OPTIONS = [
  { days: '7', label: '7d' },
  { days: '14', label: '14d' },
  { days: '30', label: '30d' },
  { days: '90', label: '90d' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['days'];
const ALLOWED_DAYS = new Set<string>(RANGE_OPTIONS.map((r) => r.days));

function money(n: number | null | undefined, digits = 2): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: digits,
  }).format(n);
}

function parseError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Request failed';
}

function Tile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white px-3 py-2'>
      <p className='text-xs text-gray-500'>{label}</p>
      <p className='mt-0.5 text-lg font-semibold tabular-nums text-gray-900'>
        {value}
      </p>
      {hint ? <p className='mt-0.5 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

function AdminMetaPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate({ from: '/admin/meta' });
  const { days } = Route.useSearch();
  const daysNum = Number(days);
  const queryClient = useQueryClient();
  const [budgetUsd, setBudgetUsd] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    track('Meta Ads Admin');
  }, []);

  const settingsQuery = useQuery({
    queryKey: ['admin', 'meta-ops-settings'],
    queryFn: () =>
      apiJson<MetaOpsSettings>(getToken, '/v1/admin/meta/ops/settings'),
  });
  const scorecardQuery = useQuery({
    queryKey: ['admin', 'meta-ops-scorecard', daysNum],
    queryFn: () =>
      apiJson<MetaOpsScorecard>(
        getToken,
        `/v1/admin/meta/ops/scorecard?days=${daysNum}`,
      ),
  });
  const campaignsQuery = useQuery({
    queryKey: ['admin', 'meta-ops-campaigns', daysNum],
    queryFn: () =>
      apiJson<MetaOpsCampaignsResponse>(
        getToken,
        `/v1/admin/meta/ops/campaigns?days=${daysNum}`,
      ),
  });
  const actionsQuery = useQuery({
    queryKey: ['admin', 'meta-ops-actions'],
    queryFn: () =>
      apiJson<MetaOpsActionsResponse>(
        getToken,
        '/v1/admin/meta/ops/actions?limit=40',
      ),
  });
  const standingQuery = useQuery({
    queryKey: ['admin', 'meta-ops-standing'],
    queryFn: () =>
      apiJson<MetaOpsStandingOrders>(
        getToken,
        '/v1/admin/meta/ops/standing-orders',
      ),
  });

  useEffect(() => {
    if (!settingsQuery.data) return;
    setBudgetUsd(String(settingsQuery.data.meta_monthly_budget_usd));
  }, [settingsQuery.data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'meta-ops-settings'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'meta-ops-scorecard'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'meta-ops-campaigns'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'meta-ops-actions'] });
  };

  const patchMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiJson<MetaOpsSettings>(getToken, '/v1/admin/meta/ops/settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setFormError(null);
      invalidate();
    },
    onError: (err) => setFormError(parseError(err)),
  });

  const applyMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson<MetaOpsAction>(
        getToken,
        `/v1/admin/meta/ops/actions/${id}/apply`,
        { method: 'POST' },
      ),
    onSuccess: () => invalidate(),
    onError: (err) => setFormError(parseError(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson<MetaOpsAction>(
        getToken,
        `/v1/admin/meta/ops/actions/${id}/reject`,
        { method: 'POST' },
      ),
    onSuccess: () => invalidate(),
    onError: (err) => setFormError(parseError(err)),
  });

  const card = scorecardQuery.data;
  const harbor = card?.harbor_facebook;
  const pending = useMemo(
    () =>
      (actionsQuery.data?.actions ?? []).filter((a) => a.status === 'proposed'),
    [actionsQuery.data?.actions],
  );

  const setRange = (next: RangeDays) => {
    if (next === days) return;
    track('Meta Ads Admin Range Changed', { days: next });
    navigate({ search: { days: next } });
  };

  const saveBudget = () => {
    const monthly = Math.round(Number(budgetUsd) * 100);
    if (!Number.isFinite(monthly) || monthly < 10000 || monthly > 100000) {
      setFormError('Monthly Meta budget must be between $100 and $1,000.');
      return;
    }
    track('Meta Ads Admin Knobs Saved');
    patchMutation.mutate({ meta_monthly_budget_cents: monthly });
  };

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h1 className='text-xl font-semibold text-gray-900'>
            Meta / Instagram ads
          </h1>
          <p className='mt-1 max-w-3xl text-sm text-gray-600'>
            Website Pixel Lead is App Store / Play tap, not Sign Up. CAPI
            Purchase (hashed phone/email) is measurement only — this campaign
            does not bid on it. Harbor-truth is facebook/instagram UTM or
            fbclid, not Ads Manager CPA. Creates stay proposed until you
            apply. Daily Celery only snapshots spend. Pause is the
            weekly-agent kill switch.
          </p>
        </div>
        <div className='flex flex-wrap gap-1'>
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              type='button'
              onClick={() => setRange(opt.days)}
              className={`rounded-full px-3 py-1 text-sm ${
                days === opt.days
                  ? 'bg-harbor-50 text-primary border border-gray-200'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {formError ? (
        <p className='flex items-start gap-1.5 text-sm text-red-700'>
          <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
          {formError}
        </p>
      ) : null}

      {card ? (
        <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
          <Tile
            label='Meta spend'
            value={money(card.meta_ads.spend_usd)}
            hint={card.meta_ads.error ?? `${card.meta_ads.clicks ?? 0} clicks`}
          />
          <Tile
            label='Ads-UI Leads'
            value={
              card.meta_ads.ads_ui_leads != null
                ? card.meta_ads.ads_ui_leads.toFixed(1)
                : '—'
            }
            hint='Do not optimize this'
          />
          <Tile
            label='Harbor-truth paid'
            value={String(harbor?.paid ?? 0)}
            hint={`${harbor?.attributed ?? 0} attributed · ${harbor?.landed ?? 0} landed`}
          />
          <Tile
            label='Harbor CAC'
            value={money(card.harbor_facebook_cac_usd)}
            hint={`Target $${card.target_cac_usd.low}–$${card.target_cac_usd.high}`}
          />
        </div>
      ) : null}

      <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
        <h2 className='text-sm font-semibold text-gray-900'>Knobs</h2>
        <div className='mt-3 flex flex-wrap items-end gap-3'>
          <label className='text-sm text-gray-700'>
            Monthly budget (USD)
            <input
              className='mt-1 block w-36 rounded border border-gray-300 px-2 py-1'
              value={budgetUsd}
              onChange={(e) => setBudgetUsd(e.target.value)}
            />
          </label>
          <button
            type='button'
            className='rounded bg-primary px-3 py-1.5 text-sm font-medium text-white'
            onClick={saveBudget}
            disabled={patchMutation.isPending}
          >
            Save budget
          </button>
          <button
            type='button'
            className='inline-flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm'
            onClick={() => {
              const next = !settingsQuery.data?.meta_agent_paused;
              track('Meta Ads Admin Pause Toggled', { paused: next });
              patchMutation.mutate({ meta_agent_paused: next });
            }}
          >
            {settingsQuery.data?.meta_agent_paused ? (
              <>
                <FiPlay className='h-3.5 w-3.5' /> Unpause agent
              </>
            ) : (
              <>
                <FiPause className='h-3.5 w-3.5' /> Pause agent
              </>
            )}
          </button>
        </div>
        <p className='mt-2 text-xs text-gray-500'>
          Daily derived {money(card?.pacing.derived_daily_budget_usd)} · approval{' '}
          {card?.agent.require_human_approval ? 'required' : 'off'}
        </p>
      </div>

      <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
        <h2 className='text-sm font-semibold text-gray-900'>
          Pending actions ({pending.length})
        </h2>
        {pending.length === 0 ? (
          <p className='mt-2 text-sm text-gray-500'>No proposed Meta mutates.</p>
        ) : (
          <ul className='mt-3 space-y-3'>
            {pending.map((action) => (
              <li
                key={action.id}
                className='rounded border border-gray-100 p-3 text-sm'
              >
                <p className='font-medium text-gray-900'>
                  {action.kind} · {action.summary}
                </p>
                {action.hypothesized_outcome ? (
                  <p className='mt-1 text-gray-600'>
                    {action.hypothesized_outcome}
                  </p>
                ) : null}
                <div className='mt-2 flex gap-2'>
                  <button
                    type='button'
                    className='rounded bg-primary px-2 py-1 text-xs font-medium text-white'
                    onClick={() => {
                      track('Meta Ads Admin Action Applied', {
                        kind: action.kind,
                      });
                      applyMutation.mutate(action.id);
                    }}
                  >
                    Apply
                  </button>
                  <button
                    type='button'
                    className='rounded border border-gray-300 px-2 py-1 text-xs'
                    onClick={() => {
                      track('Meta Ads Admin Action Rejected', {
                        kind: action.kind,
                      });
                      rejectMutation.mutate(action.id);
                    }}
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
        <h2 className='text-sm font-semibold text-gray-900'>Campaigns</h2>
        {campaignsQuery.data?.error ? (
          <p className='mt-2 flex items-start gap-1.5 text-xs text-amber-800'>
            <FiAlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            {campaignsQuery.data.error}
          </p>
        ) : null}
        <div className='mt-3 overflow-x-auto'>
          <table className='min-w-full text-left text-sm'>
            <thead>
              <tr className='text-xs text-gray-500'>
                <th className='pb-2 pr-4'>Campaign</th>
                <th className='pb-2 pr-4'>Status</th>
                <th className='pb-2 pr-4'>Objective</th>
                <th className='pb-2 pr-4 text-right'>Spend</th>
                <th className='pb-2 text-right'>Ads-UI Leads</th>
              </tr>
            </thead>
            <tbody>
              {(campaignsQuery.data?.campaigns ?? []).length === 0 ? (
                <tr>
                  <td colSpan={5} className='py-3 text-gray-500'>
                    No campaigns in this ad account yet.
                  </td>
                </tr>
              ) : (
                (campaignsQuery.data?.campaigns ?? []).map((campaign) => (
                  <tr key={campaign.id} className='border-t border-gray-100'>
                    <td className='py-2 pr-4 font-medium text-gray-900'>
                      {campaign.name}
                      <div className='text-xs font-normal text-gray-500'>
                        {(campaign.adsets ?? []).length} ad sets
                      </div>
                    </td>
                    <td className='py-2 pr-4'>{campaign.status}</td>
                    <td className='py-2 pr-4'>{campaign.objective}</td>
                    <td className='py-2 pr-4 text-right tabular-nums'>
                      {money(campaign.spend_usd)}
                    </td>
                    <td className='py-2 text-right tabular-nums'>
                      {campaign.ads_ui_leads}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {standingQuery.data?.policy?.hq_orders?.length ? (
        <div className='rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700 shadow-sm'>
          <h2 className='text-sm font-semibold text-gray-900'>Standing orders</h2>
          <ul className='mt-2 list-disc space-y-1 pl-5'>
            {standingQuery.data.policy.hq_orders.map((order) => (
              <li key={order}>{order}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/meta')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { days: RangeDays } => {
    const raw = String(search.days ?? '');
    return {
      days: ALLOWED_DAYS.has(raw) ? (raw as RangeDays) : '30',
    };
  },
  component: AdminMetaPage,
});
